import { PAGE_TYPE_PRODUCT, TENANT_CH } from 'shared/consts'
import {
  EXPERIMENT_VARIATION,
  PDP_APP_BANNER,
  PDP_APP_BANNER_AT,
  PDP_APP_BANNER_CH,
  PDP_APP_BANNER_FR,
  PDP_APP_BANNER_IT,
  USP_BANNER_IT,
} from 'shared/experiments/consts'
import {
  activateExperiment,
  getExperiment,
  getExperiments,
} from 'shared/experiments/utils/experiments'
import { bindTrackingEvents } from 'shared/experiments/utils/tracking'
import store from 'shared/store'
import { type PageType } from 'types/cmsTypes'
import type { Experiment } from 'types/shopConfig'

import initBanner from './banner'
import {
  BANNER_EVENT,
  BANNER_KEYS,
  EVENT_CLICK_CLOSE,
  EVENT_CLICK_CTA,
  EXPERIMENT_TENANT,
} from './consts'

import './styles.scss'

const getTenant = () => {
  const { tenant, language } =
    store.getPublicRuntimeConfig()?.publicConfig || {}

  // temporary fix for CH, will be better when we move this to shopConfig
  if (tenant === TENANT_CH) {
    return `${tenant}-${language}`
  }
  return tenant
}

const isExperimentEnabled = (experiments: Experiment[]) =>
  experiments.some(experiment => {
    const { variant, isEnabled } = experiment
    const isVariantValid = variant === 'variation_1' || variant === 'v1'

    return isEnabled && isVariantValid
  })

const isEnvValid = (
  pageType: PageType,
  productId: string,
  experiments: Experiment[]
): boolean => {
  const experimentsList = getExperiments(
    [
      PDP_APP_BANNER,
      PDP_APP_BANNER_AT,
      PDP_APP_BANNER_IT,
      PDP_APP_BANNER_FR,
      PDP_APP_BANNER_CH,
    ],
    experiments
  )
  return Boolean(
    pageType === PAGE_TYPE_PRODUCT &&
      window.dataLayer &&
      productId &&
      isExperimentEnabled(experimentsList)
  )
}

const handleBannerEvents = () => {
  if (!window.AF) {
    return
  }

  window.AF('banners', 'showBanner')
}

const clearGtmStyles = () => {
  const element = document.getElementById('body')
  element?.classList.remove('with-nc-banner')
}

const isBannerInserted = (addedNode: Node): boolean =>
  addedNode.nodeType === 1 &&
  (<Element>addedNode).matches('.responsive-wrapper')

const handleMutations = (
  mutations: MutationRecord[],
  callback: CallableFunction
) => {
  for (const mutation of mutations) {
    const banner = Array.from(mutation.addedNodes).find(isBannerInserted)

    if (banner) {
      callback()
      break
    }
  }
}
/**
 * For IT we need to move the banner to the top, if USP banner is not there
 * to avoid empty space on mobile version
 * */
const fixTopPositionDependingOnUspBanner = (experiments: Experiment[]) => {
  const experiment = getExperiment(USP_BANNER_IT, experiments)
  const isUspBannerNotShown =
    experiment.isEnabled && experiment.variant === EXPERIMENT_VARIATION.V1

  if (!isUspBannerNotShown) {
    document.body.classList.add('usp-banner-shown')
  }
}

const observeBanner = (callback: CallableFunction) => {
  // eslint-disable-next-line prefer-const
  let observer: MutationObserver

  const stopObserving = () => {
    callback()
    observer.disconnect()
  }

  observer = new MutationObserver(mutations =>
    handleMutations(mutations, stopObserving)
  )

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  })
}

const bindEventsTracking = () => {
  bindTrackingEvents([
    {
      selector: '.responsive-wrapper button[data-role=cta]',
      event: 'click',
      trackingEvent: EVENT_CLICK_CTA,
    },
    {
      selector: '.responsive-wrapper [data-af-close-button=true]',
      event: 'click',
      trackingEvent: EVENT_CLICK_CLOSE,
    },
  ])
}

const appendBannerToHeader = () => initBanner(BANNER_KEYS[getTenant()])

const handleBannerInsertion = () => {
  clearGtmStyles()
  window.dataLayer.push({ event: BANNER_EVENT })
  bindEventsTracking()
}

const callExperimentActivation = () => {
  const experimentName = EXPERIMENT_TENANT[getTenant()]

  if (experimentName) {
    void activateExperiment(experimentName)
  }
}

const init = () => {
  const {
    pageType = '',
    productId = '',
    experiments = [],
  } = store.getPublicRuntimeConfig()?.pageProperties || {}

  const [experiment] = getExperiments(
    [
      PDP_APP_BANNER,
      PDP_APP_BANNER_AT,
      PDP_APP_BANNER_FR,
      PDP_APP_BANNER_IT,
      PDP_APP_BANNER_CH,
    ],
    experiments
  )

  if (!experiment?.isEnabled) {
    return
  }

  callExperimentActivation()

  if (isEnvValid(pageType, productId, experiments)) {
    fixTopPositionDependingOnUspBanner(experiments)
    observeBanner(handleBannerInsertion)
    appendBannerToHeader()
    handleBannerEvents()
  }
}

export default init
